import { useQuery } from '@tanstack/react-query';
import { getUserPermissions } from '../api/organization.api';
import { useSettingsStore } from '@/store/settingsStore';
import { useAuthStore } from '@/store/authStore';
import { useEffect } from 'react';

/**
 * Hook to fetch and validate permissions
 * Matches the Nuxt2 implementation where permissions are fetched once per organization
 * and project-specific permissions are extracted from the response
 */
export function usePermissions() {
  const user = useAuthStore((state) => state.user);
  const selectedOrganization = useSettingsStore((state) => state.selectedOrganization);
  const selectedProject = useSettingsStore((state) => state.selectedProject);
  const setOrganizationPermissions = useSettingsStore((state) => state.setOrganizationPermissions);
  const setProjectPermissions = useSettingsStore((state) => state.setProjectPermissions);
  const organizationPermissions = useSettingsStore((state) => state.organizationPermissions);
  const projectPermissions = useSettingsStore((state) => state.projectPermissions);

  // Fetch all permissions for the user in the selected organization
  const permissionsQuery = useQuery({
    queryKey: ['user-permissions', selectedOrganization?.id, user?.id],
    queryFn: () => getUserPermissions(selectedOrganization!.id, user!.id),
    enabled: !!selectedOrganization && !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Update organization permissions when data changes
  useEffect(() => {
    if (permissionsQuery.data) {
      setOrganizationPermissions(permissionsQuery.data.orgPermissions);
    }
  }, [permissionsQuery.data, setOrganizationPermissions]);

  // Update project permissions when selected project changes
  useEffect(() => {
    if (permissionsQuery.data && selectedProject) {
      // Find permissions for the selected project or use "all" permissions
      const projectPerms = permissionsQuery.data.projects.find(
        (p) => p.id === selectedProject.id || p.id === 'all'
      );
      
      if (projectPerms) {
        setProjectPermissions(projectPerms.permissions);
      } else {
        // No specific permissions found, clear them
        setProjectPermissions(null);
      }
    } else if (!selectedProject) {
      // Clear project permissions when no project selected
      setProjectPermissions(null);
    }
  }, [permissionsQuery.data, selectedProject, setProjectPermissions]);

  // Clear permissions when organization changes
  useEffect(() => {
    if (!selectedOrganization) {
      setOrganizationPermissions(null);
      setProjectPermissions(null);
    }
  }, [selectedOrganization, setOrganizationPermissions, setProjectPermissions]);

  return {
    organizationPermissions: organizationPermissions,
    projectPermissions: projectPermissions,
    isLoadingPermissions: permissionsQuery.isLoading,
    permissionsError: permissionsQuery.error,
    refetchPermissions: permissionsQuery.refetch,
    allProjectPermissions: permissionsQuery.data?.projects || [],
  };
}